/**
 * Descriptors slice (PX-W6.5) — the live agent catalogue read view.
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import * as service from './services/descriptorsService.http'
import type { AgentDescriptor } from './types'

/** Preserve the canonical ApiError (the transport already mapped it). */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

export const fetchAgentDescriptors = createAsyncThunk<
  AgentDescriptor[],
  void,
  { rejectValue: ApiError }
>(
  'descriptors/fetchAgentDescriptors',
  async (_, { rejectWithValue }) => {
    try {
      return await service.descriptorsService.agents()
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { descriptors: DescriptorsState }).descriptors.cache, null)
  }
)

export interface DescriptorsState {
  agents: AgentDescriptor[]
  cache: FetchCache
  error: ApiError | null
}

const initialState: DescriptorsState = {
  agents: [],
  cache: initialFetchCache,
  error: null
}

export const descriptorsSlice = createSlice({
  name: 'descriptors',
  initialState,
  reducers: {
    descriptorsRetryClicked(state) {
      invalidate(state.cache)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgentDescriptors.pending, (state) => {
        fetchStarted(state.cache)
        state.error = null
      })
      .addCase(fetchAgentDescriptors.fulfilled, (state, action: PayloadAction<AgentDescriptor[]>) => {
        fetchSucceeded(state.cache, null)
        state.agents = action.payload
      })
      .addCase(fetchAgentDescriptors.rejected, (state, action) => {
        fetchFailed(state.cache)
        state.error = action.payload ?? null
      })
  }
})

export const { descriptorsRetryClicked } = descriptorsSlice.actions

/* ── Selectors ─────────────────────────────────────────────────────────── */

interface WithDescriptors {
  descriptors: DescriptorsState
}

export const selectAgentDescriptors = (s: WithDescriptors): AgentDescriptor[] =>
  s.descriptors.agents
export const selectDescriptorsCache = (s: WithDescriptors): FetchCache =>
  s.descriptors.cache
export const selectDescriptorsError = (s: WithDescriptors): ApiError | null =>
  s.descriptors.error
